import { defineStore } from 'pinia'
import { useSurveysStore } from './surveys-store'
import { useStatesStore } from './states-store'
import { loadGroupResultsForSurveyAPI } from '@/services/results-service'
import { statsComputer } from './helpers/results-store-helper'
import {
  trendComputer,
  oldSurveysWithinTimePeriod,
  trendsForTimePeriod
} from './helpers/trend-store-helper'
import { get } from '@/helpers/global-helpers'
import {
  deleteNestedProperty,
  setNestedProperty,
  pathForResults
} from './helpers/comparison-store-helper'

export const useComparisonStore = defineStore('comparison-store', {
  state: () => ({
    error: {},
    loading: {},
    resultsForSurveys: {},
    userAddedRows: {},
    addRowGroup: null
  }),
  actions: {
    setAddRowGroup(group) {
      this.addRowGroup = group
    },
    clear() {
      this.loading = {}
      this.error = {}
      this.resultsForSurveys = {}
    },
    // used when switching selected segment for comparison view to keep results for userAddedRows
    clearAllButCustomRows() {
      // clear all results from resultsForSurveys except those that are there because of userAddedRows
      const newResult = Object.keys(this.resultsForSurveys).reduce(
        (newResultsForSurveys, surveyName) => {
          if (this.userAddedRows[surveyName]) {
            newResultsForSurveys = Object.keys(
              this.resultsForSurveys[surveyName] || {}
            ).reduce((res, groupName) => {
              const segmentsToKeepForGroup = this.userAddedRows[surveyName]
                .filter((row) => row.groupName === groupName)
                .map((row) => row.attributeSegment)
              for (const attributeSegment of segmentsToKeepForGroup) {
                const path = pathForResults({
                  surveyName,
                  groupName,
                  attributeSegment
                })
                const segmentKey =
                  attributeSegment.name === 'all'
                    ? 'all'
                    : `${attributeSegment.attributeName}.${attributeSegment.segmentName}`
                const dataToKeep = {
                  ...get(
                    this.resultsForSurveys[surveyName][groupName],
                    segmentKey
                  )
                }
                res = setNestedProperty({
                  storeObject: res,
                  path,
                  value: dataToKeep
                })
              }
              return res
            }, {})
          }
          return newResultsForSurveys
        },
        {}
      )
      this.resultsForSurveys = newResult
    },
    /**
     * Helper action that performs the common logic:
     * - Chunks the group names and calls the API for each group.
     * - Processes each response using statsComputer and trendComputer.
     * - Stores the computed data in the nested resultsForSurveys object.
     *
     * @param {Object} payload
     * @param {string} payload.surveyName
     * @param {Array} payload.groupNames
     * @param {Function} payload.getApiParams Callback returning extra API parameters per group.
     * @param {Object} payload.surveyDetails Survey details used in computations.
     * @param {Object} payload.selectedAttributeSegment
     */
    async fetchAndProcessGroupResults({
      surveyName,
      surveyDetails,
      selectedTimePeriod,
      segmentsLoadedForGroups = null,
      getApiParams
    }) {
      const chunkSize = 5
      const groupChunks = chunk(segmentsLoadedForGroups, chunkSize)
      // groupChunks has groupName and attributeSegmentName
      let newResultsForSurveys = { ...this.resultsForSurveys }
      for (const groupChunk of groupChunks) {
        const operations = groupChunk.map(async (groupAndSegment) => {
          const groupName = groupAndSegment.groupName
          const groupResultsResponse = await loadGroupResultsForSurveyAPI({
            surveyName,
            groupName,
            selectedAttributeSegment: groupAndSegment.attributeSegment,
            ...getApiParams
          })
          Object.keys(groupResultsResponse.results).forEach(
            (surveyNameForResults) => {
              const resultsToStoreForSurvey = getResultAndTrendDataForSurvey({
                allResults: groupResultsResponse.results,
                surveyDetails,
                surveyNameForResults,
                surveyName,
                selectedTimePeriod
              })
              // Update the store using a helper that sets a nested property.
              newResultsForSurveys = setNestedProperty({
                storeObject: newResultsForSurveys,
                path: pathForResults({
                  surveyName: surveyNameForResults,
                  groupName,
                  attributeSegment: groupAndSegment.attributeSegment
                }),
                value: resultsToStoreForSurvey
              })
            }
          )
        })
        await Promise.all(operations)
      }
      this.resultsForSurveys = newResultsForSurveys
    },

    getCurrentState({ showTrend }) {
      const statesStore = useStatesStore()
      const selectedTimePeriod = statesStore.getSelectedTimePeriod
      const selectedAttributeSegment = statesStore.selectedAttributeSegment

      const surveysStore = useSurveysStore()
      const surveyDetails = surveysStore.getSurveyDetails
      const aggregate = !!surveyDetails?.details.aggregate
      const oldSurveys = showTrend
        ? oldSurveysWithinTimePeriod({
            surveyDetails,
            selectedTimePeriod
          })
        : []
      return {
        selectedTimePeriod,
        selectedAttributeSegment,
        surveyDetails,
        aggregate,
        oldSurveys
      }
    },

    // Load results for all given groupNames. In the case of adding a row, groupNames is just one group.
    // In this case, they will all have the same attributeSegment.
    // Used when changing selected segment, loading more groups, expanding group and adding custom row.
    async loadGroupResultsForComparisonAction({
      surveyName,
      groupNames,
      attributeSegment = null,
      showTrend
    }) {
      if (!surveyName || !groupNames.length) {
        throw new Error(
          `Missing params. Survey: ${surveyName}. Groups: ${groupNames}.`
        )
      }
      // Initiate resultsForSurveys for surveyName so the table renders loading indicators
      if (!this.resultsForSurveys[surveyName]) {
        this.resultsForSurveys[surveyName] = {}
      }

      const loadingState = groupNames.reduce((res, groupName) => {
        res[groupName] = res[groupName] || []
        res[groupName].push(attributeSegment?.name || 'all')
        return res
      }, {})
      this.loading[surveyName] = {
        ...this.loading[surveyName],
        ...loadingState
      }

      const {
        selectedTimePeriod,
        selectedAttributeSegment,
        surveyDetails,
        aggregate,
        oldSurveys
      } = this.getCurrentState({ showTrend })

      await this.fetchAndProcessGroupResults({
        surveyName,
        segmentsLoadedForGroups: groupNames.map((groupName) => ({
          groupName,
          attributeSegment: attributeSegment || selectedAttributeSegment
        })),
        surveyDetails,
        selectedTimePeriod,
        getApiParams: {
          aggregate,
          oldSurveys
        }
      })

      // Mark loading as false for the groups after processing.
      this.loading[surveyName] = {
        ...this.loading[surveyName],
        ...groupNames.reduce((acc, groupName) => {
          acc[groupName] = false
          return acc
        }, {})
      }
    },

    // Remove unwanted results and calculate new trend data based on the remaining results
    removeOldSurveysAndMakeNewTrendData({
      surveysLoadedToRemove,
      surveyName,
      surveyDetails,
      selectedTimePeriod,
      segmentsLoadedForGroups
    }) {
      let newResultsForSurveys = Object.fromEntries(
        Object.entries(this.resultsForSurveys).filter(
          ([survey]) => !surveysLoadedToRemove.includes(survey)
        )
      )
      const surveysLoadedToUpdate = Object.keys(newResultsForSurveys)
      segmentsLoadedForGroups.forEach((groupAndSegment) => {
        // first translate all existing results to the standard results format we get from API
        const allResultsForGroup = surveysLoadedToUpdate.reduce(
          (resultsForGroup, surveyNameToUpdate) => {
            const resultPath = pathForResults({
              surveyName: surveyNameToUpdate,
              groupName: groupAndSegment.groupName,
              attributeSegment: groupAndSegment.attributeSegment
            })
            const surveyResultsForGroup = get(newResultsForSurveys, resultPath)

            resultsForGroup[surveyNameToUpdate] = surveyResultsForGroup
            return resultsForGroup
          },
          {}
        )

        // then compute the new trends for the current survey based on the kept statistics
        const {
          trendTagsToStore,
          trendCategoriesToStore,
          trendQuestionsToStore
        } = getTrendDataToStoreForSurvey({
          allResults: allResultsForGroup,
          surveyName,
          surveyDetails,
          selectedTimePeriod
        })
        const dataToStore = {
          ...allResultsForGroup[surveyName],
          trendTags: trendTagsToStore,
          trendCategories: trendCategoriesToStore,
          trendQuestions: trendQuestionsToStore
        }
        const resultPath = pathForResults({
          surveyName,
          groupName: groupAndSegment.groupName,
          attributeSegment: groupAndSegment.attributeSegment
        })
        newResultsForSurveys = setNestedProperty({
          storeObject: newResultsForSurveys,
          path: resultPath,
          value: dataToStore
        })
      })
      this.resultsForSurveys = newResultsForSurveys
    },

    // Used when toggling trend or changing time period to add OR remove survey results
    // To avoid loading trends for expanded/collapsed groups unless needed,
    // groupnames can be defined to only keep and update some of the groups, or leave empty to update all groups
    async updateAllRowsAction({ surveyName, groupNames }) {
      if (groupNames?.length) {
        // Since we only want these groups, remove all group results that are not in groupNames
        this.resultsForSurveys[surveyName] = Object.keys(
          this.resultsForSurveys[surveyName]
        ).reduce((res, groupName) => {
          if (groupNames.includes(groupName)) {
            res[groupName] = this.resultsForSurveys[surveyName][groupName]
          }
          return res
        }, {})
      }
      const surveysLoaded = Object.keys(this.resultsForSurveys)
      const resultsForGroups = groupNames
        ? Object.keys(this.resultsForSurveys[surveyName]).reduce(
            (acc, groupName) => {
              if (groupNames.includes(groupName)) {
                acc[groupName] = this.resultsForSurveys[surveyName][groupName]
              }
              return acc
            },
            {}
          )
        : this.resultsForSurveys[surveyName]

      // make objects with { groupName, attributeSegmentName } based on the given results object
      const segmentsLoadedForGroups = convertCurrentDataToReturnFormat({
        resultsForSurvey: resultsForGroups || {}
      })

      const loadingState = segmentsLoadedForGroups.reduce(
        (res, groupAndSegment) => {
          res[groupAndSegment.groupName] = res[groupAndSegment.groupName] || []
          res[groupAndSegment.groupName].push(
            groupAndSegment.attributeSegment.name
          )
          return res
        },
        {}
      )
      // Update loading state.
      this.loading[surveyName] = {
        ...this.loading[surveyName],
        ...loadingState
      }

      const { selectedTimePeriod, surveyDetails, aggregate, oldSurveys } =
        this.getCurrentState({ showTrend: true })

      // Compute which surveys need to be updated.
      const oldSurveysNotLoaded = oldSurveys.filter(
        (survey) => !surveysLoaded.includes(survey)
      )
      const surveysLoadedToRemove = surveysLoaded.filter(
        (survey) => survey !== surveyName && !oldSurveys.includes(survey)
      )

      if (oldSurveysNotLoaded.length) {
        await this.fetchAndProcessGroupResults({
          surveyName,
          surveyDetails,
          selectedTimePeriod,
          segmentsLoadedForGroups,
          getApiParams: {
            aggregate,
            oldSurveys: oldSurveysNotLoaded,
            onlyReturnOldSurveys: true
          }
        })
      } else if (surveysLoadedToRemove.length) {
        this.removeOldSurveysAndMakeNewTrendData({
          surveysLoadedToRemove,
          surveyName,
          surveyDetails,
          selectedTimePeriod,
          segmentsLoadedForGroups
        })
      }

      // Update loading state after processing.
      this.loading[surveyName] = {
        ...this.loading[surveyName],
        ...segmentsLoadedForGroups.reduce((acc, { groupName }) => {
          acc[groupName] = false
          return acc
        }, {})
      }
    },

    // add custom row to userAddedRows and load the required data if necessary
    async addCustomRowToTableAction({
      surveyName,
      rowName, // is the table root node, or parent group name
      groupName, // is the added group to fetch data for
      attributeSegment,
      showTrend
    }) {
      if (!surveyName || !rowName || !groupName) {
        throw new Error(
          `Missing params. Survey: ${surveyName}. Row: ${rowName}. Group: ${groupName}.`
        )
      }
      // only add the group if it's not a duplicate
      const newChildRowsForRowName = this.userAddedRows[surveyName] || []
      if (
        !newChildRowsForRowName.some(
          (item) =>
            item.parentGroup === rowName &&
            item.groupName === groupName &&
            // TODO: check if this works with attributeSegment = 'all'
            item.attributeSegment?.name === attributeSegment?.name
        )
      ) {
        newChildRowsForRowName.push({
          groupName,
          attributeSegment,
          parentGroup: rowName
        })
        const segmentKey =
          attributeSegment.name === 'all'
            ? 'all'
            : `${attributeSegment.attributeName}.${attributeSegment.segmentName}`
        // only fetch data that is not already in the store
        if (!get(this.resultsForSurveys[surveyName][groupName], segmentKey)) {
          await this.loadGroupResultsForComparisonAction({
            surveyName,
            groupNames: [groupName],
            attributeSegment,
            showTrend
          })
        }
      }
      this.userAddedRows[surveyName] = newChildRowsForRowName
    },

    // remove a custom row from userAddedRows and its results
    removeRowFromTableAction({ surveyName, group }) {
      const newUserAddedRowsForSurvey = this.userAddedRows[surveyName].filter(
        (r) => {
          return !(
            r.groupName === group.name &&
            r.parentGroup === group.parentGroup &&
            r.attributeSegment.name === group.attributeSegment.name
          )
        }
      )
      this.userAddedRows[surveyName] = newUserAddedRowsForSurvey
      const path = pathForResults({
        surveyName,
        groupName: group.name,
        attributeSegment: group.attributeSegment
      })
      // remove the stored results for this attributeSegment in this group
      this.resultsForSurveys = deleteNestedProperty({
        storeObject: this.resultsForSurveys,
        path
      })
    }
  }
})

// helper function to calculate results for surveyNameForResults
// returns trend data only if surveyNameForResults is surveyName
const getResultAndTrendDataForSurvey = ({
  allResults,
  surveyDetails,
  surveyNameForResults,
  surveyName,
  selectedTimePeriod
}) => {
  // Compute stats and trends using helper functions.
  const { tags, categories, questions } = statsComputer({
    results: allResults[surveyNameForResults],
    surveyDetails
  })
  let dataToStore = {
    tags,
    categories,
    questions
  }
  // only make trend data for current survey
  if (surveyNameForResults === surveyName) {
    const { trendTagsToStore, trendCategoriesToStore, trendQuestionsToStore } =
      getTrendDataToStoreForSurvey({
        allResults,
        surveyName,
        surveyDetails,
        selectedTimePeriod
      })
    dataToStore = {
      ...dataToStore,
      trendTags: trendTagsToStore,
      trendCategories: trendCategoriesToStore,
      trendQuestions: trendQuestionsToStore
    }
  }
  return dataToStore
}

const convertCurrentDataToReturnFormat = ({ resultsForSurvey }) => {
  return Object.keys(resultsForSurvey).reduce((res, groupName) => {
    Object.keys(resultsForSurvey[groupName]).forEach((attributeName) => {
      if (attributeName === 'all') {
        res.push({ groupName, attributeSegment: { name: 'all' } })
      } else {
        // we need to get all segments in
        Object.keys(resultsForSurvey[groupName][attributeName] || {}).forEach(
          (segmentName) =>
            res.push({
              groupName,
              attributeSegment: {
                name: `${attributeName}-${segmentName}`,
                attributeName,
                segmentName
              }
            })
        )
      }
    })
    return res
  }, [])
}

// helper function to get trend data for the current survey based on allResults
const getTrendDataToStoreForSurvey = ({
  allResults,
  surveyName,
  surveyDetails,
  selectedTimePeriod
}) => {
  const {
    tags: trendTags,
    categories: trendCategories,
    questions: trendQuestions
  } = trendComputer({
    allResults,
    surveyName,
    surveyDetails
  })
  const trendProps = {
    surveyName,
    surveyDetails,
    selectedTimePeriod
  }

  const {
    tags: trendTagsToStore,
    categories: trendCategoriesToStore,
    questions: trendQuestionsToStore
  } = trendsForTimePeriod({
    trends: {
      tags: trendTags,
      categories: trendCategories,
      questions: trendQuestions
    },
    ...trendProps
  })

  return {
    trendTagsToStore,
    trendCategoriesToStore,
    trendQuestionsToStore
  }
}

// Utility function to break an array into chunks
const chunk = (array, size) => {
  const chunkedArray = []
  let index = 0
  while (index < array.length) {
    chunkedArray.push(array.slice(index, size + index))
    index += size
  }
  return chunkedArray
}
